/**
 * Push 購読のクライアント側ロジック
 * - Web (PWA): VAPID 公開鍵で pushManager.subscribe → /me/push/subscribe
 * - Native (Capacitor iOS/Android): APNs/FCM トークン取得 → /me/push/subscribe-native
 * - 失敗パスや iOS Safari の制約は呼び出し側で判定する
 */
import { Capacitor } from "@capacitor/core";
import { PushNotifications, type Token } from "@capacitor/push-notifications";
import {
  pushSubscribe,
  pushSubscribeNative,
  pushUnsubscribe,
  pushUnsubscribeNative,
  type NativePlatform,
} from "./api.ts";

const VAPID_PUBLIC_KEY: string = import.meta.env.VITE_OWNERAPPS_VAPID_PUBLIC_KEY || "";
const NATIVE_TOKEN_STORAGE_KEY = "nx-connect:native-push-token";

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64Url(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return window.btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export type PushStatus =
  | "unsupported"
  | "needs-pwa-install"
  | "denied"
  | "default"
  | "subscribed";

// === Native (Capacitor) ============================================

function nativePlatform(): NativePlatform | null {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android" ? p : null;
}

async function getStatusNative(): Promise<PushStatus> {
  const perm = await PushNotifications.checkPermissions();
  if (perm.receive === "denied") return "denied";
  if (perm.receive === "granted") {
    return localStorage.getItem(NATIVE_TOKEN_STORAGE_KEY) ? "subscribed" : "default";
  }
  return "default";
}

async function subscribeNative(
  platform: NativePlatform,
): Promise<{ subscribed: boolean; reason?: string }> {
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return { subscribed: false, reason: "permission-denied" };

  // 先にリスナーを await で確定させてから register() を呼ぶ。
  // 旧実装は addListener().then() より先に registration イベントが発火する競合があり、
  // その場合リスナーハンドルが取得できず remove() できずにリーク。
  let regHandle: { remove: () => Promise<void> } | null = null;
  let errHandle: { remove: () => Promise<void> } | null = null;
  const cleanup = async () => {
    await regHandle?.remove().catch(() => {});
    await errHandle?.remove().catch(() => {});
  };

  let token: string;
  try {
    token = await new Promise<string>((resolve, reject) => {
      const onRegister = (t: Token) => resolve(t.value);
      const onError = (err: { error?: string }) =>
        reject(new Error(err.error ?? "registration-error"));
      Promise.all([
        PushNotifications.addListener("registration", onRegister).then((h) => (regHandle = h)),
        PushNotifications.addListener("registrationError", onError).then((h) => (errHandle = h)),
      ])
        .then(() => PushNotifications.register())
        .catch(reject);
    });
  } finally {
    await cleanup();
  }

  await pushSubscribeNative({
    platform,
    token,
    user_agent: navigator.userAgent,
  });
  localStorage.setItem(NATIVE_TOKEN_STORAGE_KEY, token);
  return { subscribed: true };
}

async function unsubscribeNative(): Promise<void> {
  const token = localStorage.getItem(NATIVE_TOKEN_STORAGE_KEY);
  if (!token) return;
  try {
    await pushUnsubscribeNative(token);
  } finally {
    localStorage.removeItem(NATIVE_TOKEN_STORAGE_KEY);
    await PushNotifications.removeAllListeners();
  }
}

// === Public API ====================================================

export async function getPushStatus(): Promise<PushStatus> {
  const np = nativePlatform();
  if (np) return getStatusNative();

  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  // iOS Safari は PWA インストール時のみ Push が動く
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    // @ts-expect-error iOS Safari only
    window.navigator.standalone === true;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS && !isStandalone) return "needs-pwa-install";

  const perm = Notification.permission;
  if (perm === "denied") return "denied";

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) return "subscribed";
  return perm === "granted" ? "default" : "default";
}

export async function subscribePush(): Promise<{ subscribed: boolean; reason?: string }> {
  const np = nativePlatform();
  if (np) return subscribeNative(np);

  if (!VAPID_PUBLIC_KEY) return { subscribed: false, reason: "vapid-missing" };

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { subscribed: false, reason: "permission-denied" };

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  await pushSubscribe({
    endpoint: sub.endpoint,
    keys: {
      p256dh: arrayBufferToBase64Url(sub.getKey("p256dh")),
      auth: arrayBufferToBase64Url(sub.getKey("auth")),
    },
    user_agent: navigator.userAgent,
  });
  return { subscribed: true };
}

export async function unsubscribePush(): Promise<void> {
  if (nativePlatform()) {
    await unsubscribeNative();
    return;
  }
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await pushUnsubscribe(sub.endpoint);
  } finally {
    await sub.unsubscribe();
  }
}
